import { AlertV2, StandardAlert } from '@cimplico/components';
import { useFieldArray, useFormContext } from 'react-hook-form';
import plural from '../../utilities/plural';
import { PatchResponseBody } from '../../types/api-response';
import { Worksheet } from '../../types/worksheet';
import { WorksheetLinkingFormData } from './LinkPriorWorksheetsForm';
import { isNullOrUndefined } from '../new-workpaper/utils';
import { useState, useEffect } from 'react';

type ErrorResultLinkingError = {
    statusCode: number;
    error: {
        original: Record<string, unknown> | {};
        fields: {
            id: string;
            errorMessage: string;
        };
    };
};

type ErrorResultLinkingSuccess = {
    statusCode: number;
    data: Worksheet;
};

type WorksheetErrorResponse = {
    hasErrors: boolean;
    results: (ErrorResultLinkingError | ErrorResultLinkingSuccess)[];
};

type SuccessProps = {
    results: PatchResponseBody<Worksheet>;
    onClose: () => void;
};

type ErrorProps = {
    results: WorksheetErrorResponse;
    onClose: () => void;
};

type LinkPriorWorksheetsResultProps =
    | { status: 'success'; results: PatchResponseBody<Worksheet> }
    | { status: 'error'; results: WorksheetErrorResponse };

function LinkPriorWorksheetsResult({ status, results }: LinkPriorWorksheetsResultProps): JSX.Element {
    const [isOpen, setIsOpen] = useState(true);

    useEffect(() => {
        setIsOpen(true);
    }, [status, results]);

    if (!isOpen) return <></>;

    if (status === 'error') {
        return <LinkPriorWorksheetsError results={results} onClose={() => setIsOpen(false)} />;
    }
    return <LinkPriorWorksheetsSuccess results={results} onClose={() => setIsOpen(false)} />;
}

function LinkPriorWorksheetsSuccess({ results, onClose }: SuccessProps): JSX.Element {
    const successCount = results.results.filter((r) => !isNullOrUndefined(r.data)).length;
    return (
        <AlertV2
            type="success"
            title="Successfully linked Worksheets"
            description={`You linked ${successCount} prior ${plural(successCount, 'worksheet', 'worksheets')}.`}
            onClose={onClose}
        />
    );
}

function LinkPriorWorksheetsError({ results, onClose }: ErrorProps): JSX.Element {
    const [viewMore, setViewMore] = useState(false);
    const form = useFormContext<WorksheetLinkingFormData>();
    const { fields } = useFieldArray({ control: form.control, name: 'worksheetLinkings' });
    const errors = results.results
        .filter((r): r is ErrorResultLinkingError => 'error' in r)
        .map((r) => {
            const matchingWs = fields.find((f) => f.currentWorksheet.id === r.error.fields.id);
            return matchingWs
                ? `${matchingWs.currentWorksheet.name}: ${r.error.fields.errorMessage ?? 'Linking prior worksheet failed'}`
                : '';
        })
        .filter(Boolean);
    const successCount = results.results.filter((r): r is ErrorResultLinkingSuccess => 'data' in r).length;
    return (
        <div className="text-left">
            <StandardAlert
                type="error"
                title={`Failed to link ${errors.length ?? ''} ${plural(errors.length, 'worksheet', 'worksheets')}. ${
                    successCount > 0 ? `${successCount} ${plural(successCount, 'worksheet', 'worksheets')} linked successfully.` : ''
                }`}
                description={viewMore ? errors ?? ['Linking prior worksheets failed. Please try again.'] : undefined}
                onClose={onClose}
            >
                {!viewMore && <StandardAlert.Button onClick={(): void => setViewMore(!viewMore)}>View error details</StandardAlert.Button>}
                {viewMore && <StandardAlert.Button onClick={(): void => setViewMore(!viewMore)}>View less</StandardAlert.Button>}
            </StandardAlert>
        </div>
    );
}

export default LinkPriorWorksheetsResult;
